import React, { Fragment } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { cn } from '../../utils/cn';

/**
 * Reusable Dialog component using Headless UI
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog should close
 * @param {string} props.title - Dialog title
 * @param {React.ReactNode} props.children - Dialog content
 * @param {React.ReactNode} props.footer - Optional footer content (buttons)
 * @param {string} props.className - Additional classes for the panel
 */
export function Dialog({ open, onClose, title, children, footer, className }) {
  return (
    <Transition appear show={open} as={Fragment}>
      <HeadlessDialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        {/* Full-screen container for centering */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Panel */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <HeadlessDialog.Panel
                className={cn(
                  "w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all",
                  className
                )}
              >
                {title && (
                  <HeadlessDialog.Title className="text-lg font-semibold text-stone-900 mb-3">
                    {title}
                  </HeadlessDialog.Title>
                )}

                <div className="text-sm text-stone-600">
                  {children}
                </div>

                {footer && (
                  <div className="mt-5 flex justify-end gap-2">
                    {footer}
                  </div>
                )}
              </HeadlessDialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
}

/**
 * Standard button for dialog actions
 */
export function DialogButton({ children, onClick, variant = 'default', disabled, className }) {
  const variants = {
    default: 'bg-stone-800 text-white hover:bg-stone-700',
    outline: 'bg-white border border-stone-300 text-stone-700 hover:bg-stone-50',
    destructive: 'bg-red-600 text-white hover:bg-red-700',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variants[variant],
        className
      )}
    >
      {children}
    </button>
  );
}

/**
 * Input field for dialogs (e.g., rename dialog)
 */
export function DialogInput({ value, onChange, placeholder, autoFocus, onKeyDown }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onKeyDown={onKeyDown}
      className={cn(
        "w-full px-3 py-2 rounded-lg border border-stone-300",
        "text-stone-900 placeholder:text-stone-400",
        "focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
      )}
    />
  );
}
